/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	ISchemaOptionalGroupModel,
	isSchemaOptGroupModel,
	SCHEMA_VALUE_TYPE,
	TGenericBlueprintSchema
} from "@adapptio/lib-blueprint";
import { INSPECTOR_NODE_TYPE, isInspectorNodeDefault, TInspectorNode } from "../InspectorNodeTypes";
import { mapModelToNodes } from "../index";
import { getParentComponentName, isEmptyNode } from "../utils";
import { mapperDescriptor } from "../../modules/Core/ReferenceGuide/mappers/mapperDescriptor";
import { mapperExamples } from "../../modules/Core/ReferenceGuide/mappers/mapperExamples";

export function optGroupMapper(model: ISchemaOptionalGroupModel<TGenericBlueprintSchema>): TInspectorNode {
	if (!isSchemaOptGroupModel(model)) {
		return;
	}

	const enabledModel = model.enabled;
	const enabledConstModel = model.enabled.constant;

	const enabledNode = mapModelToNodes(model.enabled);

	const constantType = enabledModel.type === SCHEMA_VALUE_TYPE.CONST;

	const showValue = constantType && enabledConstModel?.value;

	const controls = [];

	const children = [];

	if (isInspectorNodeDefault(enabledNode)) {
		controls.push(...enabledNode.controls);
	}

	const valueNode = mapModelToNodes(model.value);

	if (showValue && !isEmptyNode(valueNode)) {
		children.push(() => mapModelToNodes(model.value));
	}

	return {
		type: INSPECTOR_NODE_TYPE.DEFAULT,
		key: `${model.nodeId}`,
		label: model.schema.opts.label,
		tooltip: model.schema.opts.description,
		default: model.schema.opts["default"] || null,
		required: model.schema.opts["constraints"]?.required || false,
		referenceData: getParentComponentName(model),
		descriptor: mapperDescriptor(model.schema),
		examples: mapperExamples(model.schema),
		options: model.schema.opts["options"],
		deps: [ model, model.value, ...enabledNode.deps ],
		controls,
		children
	};
}
